/*
  <nc-scroll-scrub>: dibuja en un canvas el frame que toca según el scroll (sección nc-scroll-scrub).
  Port del ScrollScrub de TKB: precarga con decode(), lerp 0.12, frame más cercano (sin mezclar, ver draw),
  pausa fuera de pantalla, respeta prefers-reduced-motion y DPR (máx. 2).
  Los frames solo empiezan a bajarse cuando la sección se acerca a la pantalla: primero 15 repartidos
  (el canvas ya funciona con ellos, pintando el cargado más cercano) y luego el resto, los más cercanos
  a donde está el scroll primero. Con «ahorro de datos» solo se enseña el póster.
*/
if (!customElements.get('nc-scroll-scrub')) {
  customElements.define('nc-scroll-scrub', class extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.nc-scrub__track');
      this.canvas = this.querySelector('.nc-scrub__canvas');
      this.poster = this.querySelector('.nc-scrub__poster');
      this.points = [...this.querySelectorAll('.nc-scrub__point')];
      this.ats = this.points.map((p) => parseFloat(p.dataset.at || '0'));
      // Cada palabra del título y del texto en su <span>: se encienden una a una con el scroll.
      this.words = this.points.map((p) => {
        const spans = [];
        p.querySelectorAll('.nc-scrub__title, .nc-scrub__text').forEach((el) => {
          const parts = el.textContent.split(/(\s+)/);
          el.textContent = '';
          parts.forEach((w) => {
            if (!w) return;
            if (/^\s+$/.test(w)) return el.appendChild(document.createTextNode(w));
            const s = document.createElement('span');
            s.className = 'nc-scrub__w';
            s.textContent = w;
            el.appendChild(s);
            spans.push(s);
          });
        });
        return spans;
      });
      this.lit = this.points.map(() => -1);
      try { this.urls = JSON.parse(this.querySelector('.nc-scrub__frames').textContent); } catch (e) { this.urls = []; }
      this.imgs = [];
      this.loaded = [];
      this.target = 0;
      this.current = 0;
      this.visible = false;
      this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.saveData = !!(navigator.connection && navigator.connection.saveData);

      if (this.reduced.matches || !this.urls.length) return this.showStatic();

      this.update = this.update.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.fit = this.fit.bind(this);
      this.tick = this.tick.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.fit, { passive: true });
      this.poster.addEventListener('load', this.fit);
      // El canvas copia la caja del póster: si esta cambia sin redimensionar la ventana (fuentes que cargan
      // tarde y mueven los textos de arriba), hay que volver a ajustarlo.
      if (window.ResizeObserver) {
        this.ro = new ResizeObserver(this.fit);
        this.ro.observe(this.poster);
      }
      this.update();

      // Ahorro de datos: nada de frames, el póster pasa a la vista final (todas las piezas separadas).
      if (this.saveData) {
        if (this.poster.dataset.last) this.poster.src = this.poster.dataset.last;
        return;
      }

      // Cargar cuando falte ~1,5 pantallas para llegar; pintar solo mientras se ve.
      this.loadObs = new IntersectionObserver((entries) => {
        if (entries.some((e) => e.isIntersecting)) { this.loadObs.disconnect(); this.preload(); }
      }, { rootMargin: '150% 0px' });
      this.loadObs.observe(this);
      this.visObs = new IntersectionObserver((entries) => {
        this.visible = entries.some((e) => e.isIntersecting);
        if (this.visible) this.wake();
      }, { rootMargin: '100px 0px' });
      this.visObs.observe(this);
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.fit);
      if (this.loadObs) this.loadObs.disconnect();
      if (this.visObs) this.visObs.disconnect();
      if (this.ro) this.ro.disconnect();
      if (this.raf) cancelAnimationFrame(this.raf);
      if (this.scrollRaf) cancelAnimationFrame(this.scrollRaf);
      if (this.pinned) document.documentElement.classList.remove('nc-scrub-pinned');
      this.dead = true;
    }

    showStatic() {
      if (this.poster && this.poster.dataset.last) this.poster.src = this.poster.dataset.last;
      this.points.forEach((p, i) => {
        p.classList.add('is-on', 'is-current');
        p.style.setProperty('--pt-progress', '1');
        this.setLit(i, this.words[i].length);
      });
    }

    async preload() {
      const load = (src) => new Promise((resolve) => {
        const img = new Image();
        img.decoding = 'async';
        img.onload = () => (img.decode ? img.decode().catch(() => {}) : Promise.resolve()).then(() => resolve(img));
        img.onerror = () => resolve(null);
        img.src = src;
      });
      const n = this.urls.length;
      const requested = new Array(n).fill(false);
      const fetchFrame = async (i) => {
        requested[i] = true;
        const img = await load(this.urls[i]);
        if (img && !this.dead) { this.imgs[i] = img; this.loaded.push(i); this.dirty = true; this.wake(); }
      };
      // 1.ª pasada: 15 frames repartidos (incluidos el primero y el último), 6 a la vez.
      const m = Math.min(15, n);
      const first = [...new Set(Array.from({ length: m }, (_, k) => Math.round((k * (n - 1)) / Math.max(1, m - 1))))];
      let q = 0;
      const firstWorker = async () => { while (q < first.length && !this.dead) await fetchFrame(first[q++]); };
      await Promise.all(Array.from({ length: 6 }, firstWorker));
      if (this.dead || !this.loaded.length) return; // nada cargó: se queda el póster
      this.ctx = this.canvas.getContext('2d');
      this.fit();
      this.current = this.target;
      this.draw(this.current);
      this.classList.add('is-ready');
      this.wake();
      // 2.ª pasada: el resto, siempre el que quede más cerca de donde va el scroll.
      const nextNearest = () => {
        const c = Math.round(this.target);
        let best = -1;
        for (let i = 0; i < n; i++) if (!requested[i] && (best < 0 || Math.abs(i - c) < Math.abs(best - c))) best = i;
        return best;
      };
      const restWorker = async () => {
        for (let i = nextNearest(); i >= 0 && !this.dead; i = nextNearest()) await fetchFrame(i);
      };
      await Promise.all(Array.from({ length: 4 }, restWorker));
    }

    fit() {
      if (!this.ctx) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      // El canvas copia la caja del póster (que ya respeta la proporción de los frames y el hueco disponible).
      const box = this.poster.getBoundingClientRect();
      const w = Math.max(1, Math.round(box.width));
      const h = Math.max(1, Math.round(box.height));
      this.canvas.style.width = w + 'px';
      this.canvas.style.height = h + 'px';
      this.canvas.width = Math.round(w * dpr);
      this.canvas.height = Math.round(h * dpr);
      this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      this.ctx.imageSmoothingQuality = 'high';
      this.w = w;
      this.h = h;
      this.dirty = true; // al cambiar el tamaño del canvas se borra: hay que repintar aunque el frame sea el mismo
      this.draw(this.current);
    }

    // Un solo cálculo por frame de pantalla, aunque lleguen varios eventos de scroll.
    onScroll() {
      if (this.scrollRaf) return;
      this.scrollRaf = requestAnimationFrame(() => { this.scrollRaf = 0; this.update(); });
    }

    update() {
      const rect = this.track.getBoundingClientRect();
      const total = this.track.offsetHeight - window.innerHeight;
      const raw = Math.max(0, Math.min(1, total > 0 ? -rect.top / total : 0));
      // Fijada en pantalla: la cabecera no baja mientras tanto (CSS de la sección, solo móvil).
      const pinned = rect.top <= 0 && rect.bottom >= window.innerHeight;
      if (pinned !== this.pinned) {
        this.pinned = pinned;
        document.documentElement.classList.toggle('nc-scrub-pinned', pinned);
      }
      // Pausa al principio (8 %) y al final (6 %) y curva suave entre medias: el tubo «decide» abrirse
      // en vez de arrancar con el primer píxel de scroll.
      const u = Math.max(0, Math.min(1, (raw - 0.08) / 0.86));
      const t = u * u * (3 - 2 * u);
      this.target = t * (this.urls.length - 1);
      this.wake();
      let currentIdx = -1;
      this.points.forEach((p, i) => {
        const on = t >= this.ats[i];
        p.classList.toggle('is-on', on);
        if (on) currentIdx = i;
      });
      this.points.forEach((p, i) => {
        const cur = i === currentIdx;
        p.classList.toggle('is-current', cur);
        // Progreso dentro de su tramo: las palabras se encienden en la primera mitad; la línea llena el tramo entero.
        const start = this.ats[i];
        const end = i + 1 < this.ats.length ? this.ats[i + 1] : 1;
        const span = Math.max(0.001, end - start);
        const prog = cur ? Math.max(0, Math.min(1, (t - start) / span)) : 0;
        p.style.setProperty('--pt-progress', prog.toFixed(3));
        const n = this.words[i].length;
        this.setLit(i, cur ? Math.max(1, Math.ceil(Math.min(1, (t - start) / (span * 0.5)) * n)) : 0);
      });
    }

    setLit(i, count) {
      if (this.lit[i] === count) return;
      this.lit[i] = count;
      this.words[i].forEach((w, k) => w.classList.toggle('is-lit', k < count));
    }

    // Arranca el bucle de pintado si estaba parado (solo hay bucle mientras se ve y queda camino por recorrer).
    wake() {
      if (!this.ctx || this.raf || this.dead) return;
      this.raf = requestAnimationFrame(this.tick);
    }

    tick() {
      this.raf = 0;
      if (this.dead) return;
      const d = this.target - this.current;
      this.current = Math.abs(d) < 0.01 ? this.target : this.current + d * 0.12;
      if (this.visible) this.draw(this.current);
      if (this.visible && this.current !== this.target) this.raf = requestAnimationFrame(this.tick);
    }

    // Frame más cercano, sin mezclar: con frames transparentes, la mezcla entre dos deja las piezas
    // que se mueven medio transparentes (fantasmas). El lerp ya suaviza el paso de uno a otro.
    draw(f) {
      if (!this.ctx || !this.imgs.length) return;
      const want = Math.max(0, Math.min(this.urls.length - 1, Math.round(f)));
      // Si ese frame aún no ha bajado, el cargado más cercano.
      let i = want;
      if (!this.imgs[i]) {
        i = this.loaded[0];
        for (const k of this.loaded) if (Math.abs(k - want) < Math.abs(i - want)) i = k;
      }
      if (i === this.drawn && !this.dirty) return;
      const { w, h } = this;
      this.ctx.clearRect(0, 0, w, h);
      if (this.imgs[i]) this.ctx.drawImage(this.imgs[i], 0, 0, w, h);
      this.drawn = i;
      this.dirty = false;
    }
  });
}
